// src/pages/FoundationTransmission.jsx
// Parcours « transmission » d'une fondation de famille (ou de particulier) : sept étapes, chacune mesurée sur les
// données déjà saisies, avec un lien vers l'endroit où la compléter, puis les dossiers à remettre au notaire.
// ARTICHAUT organise l'information ; il ne donne ni conseil juridique ni conseil fiscal.
// Textes en français, anglais et allemand (langue de l'interface). Aucune donnée n'est écrite par cette page.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import SuccessionDossierButton from "../components/SuccessionDossierButton";
import ContinuityReportButton from "../components/ContinuityReportButton";

const TEXT = {
  fr: {
    title: "Transmission de la fondation",
    lead: "Sept étapes pour que la collection de la fondation soit compréhensible par ceux qui la reprendront. Chaque étape se mesure sur ce que vous avez déjà saisi.",
    back: "← Tableau de bord Fondation",
    loading: "Chargement…", none: "Aucune collection pour le moment.", empty: "—",
    worksOf: (a, b) => `${a} sur ${b}`,
    fix: "Compléter", noWork: "Aucune œuvre à mesurer",
    steps: [
      ["Statut juridique des ensembles", "Chaque ensemble a un statut indiqué (fondation abritée, reconnue d'utilité publique, don avec réserve d'usufruit, legs…). « À clarifier » ne compte pas."],
      ["Propriétaire confirmé", "Les œuvres dont le propriétaire n'est pas signalé « à vérifier »."],
      ["Provenance et pièces", "Les œuvres qui ont à la fois une provenance et au moins un document."],
      ["Volontés du fondateur", "Les œuvres pour lesquelles une destination est indiquée (garder, vendre, transmettre, donner…)."],
      ["Bénéficiaires ou ayants droit", "Les œuvres attribuées à au moins un bénéficiaire."],
      ["Accès accordés", "Les personnes ou liens qui peuvent consulter le dossier (notaire, conseil, conservateur…)."],
    ],
    accessCount: (n) => (n === 0 ? "Aucun accès accordé" : `${n} accès actif${n > 1 ? "s" : ""}`),
    dossiersTitle: "7. Les dossiers à remettre",
    dossiersHelp: "À compléter puis à faire valider par votre notaire ou votre conseil juridique.",
    disclaimer: "ARTICHAUT organise l'information : il ne donne ni conseil juridique ni conseil fiscal, et ne remplace ni le notaire, ni l'avocat, ni l'expert de la fondation.",
  },
  en: {
    title: "Transferring the foundation",
    lead: "Seven steps so that the foundation's collection can be understood by those who will take it over. Each step is measured on what you have already entered.",
    back: "← Foundation dashboard",
    loading: "Loading…", none: "No collection yet.", empty: "—",
    worksOf: (a, b) => `${a} of ${b}`,
    fix: "Complete", noWork: "No work to measure",
    steps: [
      ["Legal status of the groups", "Each group has a stated status (sheltered foundation, recognised as of public utility, gift with reserved usufruct, bequest…). “To clarify” does not count."],
      ["Owner confirmed", "Works whose owner is not flagged “to verify”."],
      ["Provenance and documents", "Works that have both a provenance and at least one document."],
      ["The founder's wishes", "Works for which a destination is stated (keep, sell, transfer, donate…)."],
      ["Beneficiaries or rightful claimants", "Works allocated to at least one beneficiary."],
      ["Access granted", "People or links that can view the file (notary, adviser, curator…)."],
    ],
    accessCount: (n) => (n === 0 ? "No access granted" : `${n} active access${n > 1 ? "es" : ""}`),
    dossiersTitle: "7. The files to hand over",
    dossiersHelp: "To be completed, then validated by your notary or legal adviser.",
    disclaimer: "ARTICHAUT organises information: it gives neither legal nor tax advice, and replaces neither the notary, the lawyer nor the foundation's expert.",
  },
  de: {
    title: "Übertragung der Stiftung",
    lead: "Sieben Schritte, damit die Sammlung der Stiftung für diejenigen verständlich ist, die sie übernehmen. Jeder Schritt wird anhand Ihrer bereits erfassten Angaben gemessen.",
    back: "← Stiftungs-Dashboard",
    loading: "Wird geladen…", none: "Noch keine Sammlung.", empty: "—",
    worksOf: (a, b) => `${a} von ${b}`,
    fix: "Vervollständigen", noWork: "Kein Werk zu messen",
    steps: [
      ["Rechtsstellung der Bestände", "Jeder Bestand hat eine angegebene Rechtsstellung (Treuhandstiftung, gemeinnützig anerkannt, Schenkung unter Vorbehalt des Nießbrauchs, Vermächtnis …). „Zu klären“ zählt nicht."],
      ["Eigentümer bestätigt", "Werke, deren Eigentümer nicht als „zu prüfen“ markiert ist."],
      ["Provenienz und Belege", "Werke mit Provenienz und mindestens einem Dokument."],
      ["Wille des Gründers", "Werke, für die ein Verbleib angegeben ist (behalten, verkaufen, übertragen, schenken …)."],
      ["Begünstigte oder Berechtigte", "Werke, die mindestens einem Begünstigten zugeordnet sind."],
      ["Gewährte Zugänge", "Personen oder Links, die die Akte einsehen können (Notar, Berater, Kurator …)."],
    ],
    accessCount: (n) => (n === 0 ? "Kein Zugang gewährt" : `${n} aktive${n > 1 ? " Zugänge" : "r Zugang"}`),
    dossiersTitle: "7. Die zu übergebenden Akten",
    dossiersHelp: "Zu vervollständigen und anschließend von Ihrem Notar oder Rechtsberater prüfen zu lassen.",
    disclaimer: "ARTICHAUT ordnet Informationen: Es gibt weder Rechts- noch Steuerberatung und ersetzt weder den Notar noch den Anwalt noch den Gutachter der Stiftung.",
  },
};

const chunk = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));
const hasText = (v) => v != null && String(v).trim() !== "";
const hasDocs = (a) => !(a?.documents == null || a.documents === "" || a.documents === "[]");

function tone(ok, total) {
  if (!total) return { bar: "#cfcac0", text: "#8a8378" };
  const r = ok / total;
  if (r >= 0.8) return { bar: "#2f6b3f", text: "#2f6b3f" };
  if (r > 0) return { bar: "#b9892f", text: "#8a6a1f" };
  return { bar: "#b4572f", text: "#b4572f" };
}

export default function FoundationTransmission() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const L = TEXT[lang] || TEXT.fr;

  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!user?.id) return undefined;
    (async () => {
      const { data: cols } = await supabase
        .from("collections").select("id,title,collection_type,legal_status").eq("client_id", user.id).order("title");
      // Les dépôts reçus de tiers ne sont pas la propriété de la fondation : on ne les mesure pas ici.
      const own = (cols || []).filter((c) => c.collection_type !== "foundation_deposit");
      const ids = own.map((c) => c.id);
      let arts = [];
      if (ids.length) {
        const { data: a } = await supabase
          .from("artworks_secure")
          .select("id,collection_id,provenance,documents,destination,ownership_verification")
          .in("collection_id", ids).limit(5000);
        arts = a || [];
      }
      const withBen = new Set();
      for (const part of chunk(arts.map((a) => a.id), 200)) {
        const { data: links } = await supabase.from("artwork_beneficiary").select("artwork_id").in("artwork_id", part);
        (links || []).forEach((l) => withBen.add(l.artwork_id));
      }
      const { data: perms } = await supabase
        .from("collection_permissions").select("grantee_id,collection_id,can_view").eq("client_id", user.id).eq("can_view", true);
      const grants = (perms || []).filter((p) => p.grantee_id !== user.id && ids.includes(p.collection_id)).length;
      const { data: shares } = await supabase
        .from("dossier_shares").select("id").eq("client_id", user.id).is("revoked_at", null).gt("expires_at", new Date().toISOString());
      if (!alive) return;
      setData({ own, arts, withBen, access: grants + (shares?.length || 0) });
    })().catch(() => { if (alive) setData({ own: [], arts: [], withBen: new Set(), access: 0 }); });
    return () => { alive = false; };
  }, [user?.id]);

  const steps = useMemo(() => {
    if (!data) return [];
    const { own, arts, withBen, access } = data;
    const firstOwn = own[0]?.id;
    const firstWith = (pred) => (own.find((c) => arts.some((a) => a.collection_id === c.id && pred(a)))?.id) || firstOwn;
    const legalOk = own.filter((c) => c.legal_status && c.legal_status !== "a_clarifier").length;
    const items = [
      { ok: legalOk, total: own.length, to: own.find((c) => !c.legal_status || c.legal_status === "a_clarifier")?.id ? `/collections/${own.find((c) => !c.legal_status || c.legal_status === "a_clarifier").id}/edit` : null },
      { ok: arts.filter((a) => a.ownership_verification !== "to_verify").length, total: arts.length, to: firstWith((a) => a.ownership_verification === "to_verify") },
      { ok: arts.filter((a) => hasText(a.provenance) && hasDocs(a)).length, total: arts.length, to: firstWith((a) => !(hasText(a.provenance) && hasDocs(a))) },
      { ok: arts.filter((a) => hasText(a.destination)).length, total: arts.length, to: firstWith((a) => !hasText(a.destination)) },
      { ok: arts.filter((a) => withBen.has(a.id)).length, total: arts.length, to: firstWith((a) => !withBen.has(a.id)) },
      { access, to: firstOwn },
    ];
    return items;
  }, [data]);

  const wrap = { maxWidth: 860, margin: "0 auto", padding: "32px 20px 64px", fontFamily: "system-ui, sans-serif", color: "#1a1a1a" };
  if (!data) return <div style={wrap}>{L.loading}</div>;

  const linkTo = (id) => (id ? `/collections/${id}` : null);

  return (
    <div style={wrap}>
      <Link to="/foundation/dashboard" style={{ fontSize: 13, color: "#8a8378" }}>{L.back}</Link>
      <h1 style={{ fontFamily: "Georgia, serif", fontWeight: 400, fontSize: 30, margin: "10px 0 6px" }}>{L.title}</h1>
      <p style={{ color: "#555", fontSize: 14, lineHeight: 1.5, margin: "0 0 24px" }}>{L.lead}</p>

      {data.own.length === 0 ? (
        <p style={{ color: "#8a8378" }}>{L.none}</p>
      ) : (
        <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
          {L.steps.map(([name, hint], i) => {
            const s = steps[i] || {};
            const isAccess = i === 5;
            const total = isAccess ? 1 : s.total;
            const ok = isAccess ? (s.access > 0 ? 1 : 0) : s.ok;
            const c = tone(ok, total);
            const target = i === 0 ? s.to : linkTo(s.to);
            const complete = total > 0 && ok >= total;
            return (
              <li key={name} style={{ border: "1px solid #e5e3df", borderRadius: 8, background: "#fff", padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
                  <strong style={{ fontSize: 15 }}>{i + 1}. {name}</strong>
                  <span style={{ fontSize: 13, color: c.text, fontWeight: 600 }}>
                    {isAccess ? L.accessCount(s.access) : total ? L.worksOf(ok, total) : L.noWork}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: "#6f695f", margin: "4px 0 8px", lineHeight: 1.45 }}>{hint}</div>
                {!isAccess && total > 0 && (
                  <div style={{ height: 6, background: "#eee9df", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${Math.round((ok / total) * 100)}%`, height: "100%", background: c.bar }} />
                  </div>
                )}
                {!complete && target && (
                  <Link to={target} style={{ display: "inline-block", marginTop: 8, fontSize: 12.5, color: "#a9843f", fontWeight: 600 }}>{L.fix} →</Link>
                )}
              </li>
            );
          })}
          <li style={{ border: "1px solid #e5e3df", borderRadius: 8, background: "#fff", padding: "14px 16px" }}>
            <strong style={{ fontSize: 15 }}>{L.dossiersTitle}</strong>
            <div style={{ fontSize: 12.5, color: "#6f695f", margin: "4px 0 10px" }}>{L.dossiersHelp}</div>
            <div style={{ display: "grid", gap: 8 }}>
              {data.own.map((col) => (
                <div key={col.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, minWidth: 160 }}>{col.title}</span>
                  <SuccessionDossierButton collectionId={col.id} collectionName={col.title || ""} />
                  <ContinuityReportButton collectionId={col.id} ownerName={col.title || ""} />
                </div>
              ))}
            </div>
          </li>
        </ol>
      )}
      <p style={{ marginTop: 24, fontSize: 12, color: "#8a8378", fontStyle: "italic", lineHeight: 1.5 }}>{L.disclaimer}</p>
    </div>
  );
}
